import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, File, FileText, Folder, Image, Loader2, Search } from 'lucide-react';
import { useFileExplorer } from '@speles7172/file-console';
import type { FileItem } from '@speles7172/file-console';
import { EntityModal } from '../common/EntityModal';
import { fileTransport } from '../../api/files';

/**
 * Pick files out of the library, to attach to something
 * (COMMUNICATIONS-DESIGN C5).
 *
 * ⚠️ **`useFileExplorer`, not `<FileExplorer>`.** The hook is the package's
 * behaviour without its markup — the tree walk, the paging, the search, the
 * permissions the server computed — and this dialog opens inside
 * `ComposeEmailModal`, a screen built here. CLAUDE.md's rule applies: a screen
 * that *is* the package's renders whole, and this one is not. So the browsing
 * is the package's and the pixels are ours, beside the composer's own controls.
 *
 * It also needs something `<FileExplorer>` deliberately does not have:
 * selection. A browser is for opening files; this is for choosing several and
 * handing them back.
 *
 * ⚠️ **Files within one folder are not paged, and that is the package's
 * limit rather than a choice here.** `useFileExplorer` exposes
 * `loadMoreNodes` for a folder's *child nodes* and nothing equivalent for its
 * files: the console's own transport has no `fileCursor`, though the endpoint
 * behind it does. A folder with more files than one page shows the first page.
 * The fix is upstream, in `file-console`.
 *
 * ⚠️ **It returns ids, never object keys.** The server resolves each id through
 * the file system's own authorizer, so a caller can only attach a file they
 * could already see — see `utils/file-attachments.ts` for why a key would have
 * been the wrong thing to hand over.
 */
export interface FilePickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen files. Ids go to the server; names are for the chips. */
  onPick: (files: { id: string; filename: string }[]) => void;
  /** Where to start. An event's node scopes the picker to that event's files. */
  rootNodeId?: string;
  title?: string;
}

function iconFor(item: FileItem) {
  if (item.mediaKind === 'image') return Image;
  if (item.contentType === 'application/pdf') return FileText;
  return File;
}

export function FilePickerModal({
  open,
  onClose,
  onPick,
  rootNodeId = '',
  title = 'Attach from files',
}: FilePickerModalProps) {
  const [selected, setSelected] = useState<Map<string, string>>(new Map());

  const explorer = useFileExplorer({ transport: fileTransport, rootNodeId });
  const {
    tree,
    nodes,
    status,
    search,
    setSearch,
    results,
    searching,
    open: openNode,
    hasMoreNodes,
    loadingMore,
    loadMoreNodes,
  } = explorer;

  // A fresh dialog starts with nothing chosen. Without this, closing with two
  // files ticked and reopening offers to attach them a second time.
  useEffect(() => {
    if (!open) setSelected(new Map());
  }, [open]);

  const files = useMemo<FileItem[]>(
    () => (search.trim() && results ? results.files : (tree?.files ?? [])),
    [search, results, tree],
  );

  function toggle(item: FileItem) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item.filename);
      return next;
    });
  }

  function confirm() {
    onPick([...selected].map(([id, filename]) => ({ id, filename })));
    onClose();
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      // Stacked: this opens on top of ComposeEmailModal, which is itself an
      // EntityModal. Without a higher overlay the picker renders behind the
      // composer and the backdrop swallows every click.
      overlayClassName="z-[60]"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-xs text-gray-500" data-testid="file-picker-count">
            {selected.size === 0
              ? 'No files selected'
              : `${selected.size} file${selected.size === 1 ? '' : 's'} selected`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={selected.size === 0}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-40"
              data-testid="file-picker-confirm"
            >
              Attach
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files…"
            aria-label="Search files"
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            data-testid="file-picker-search"
          />
        </div>

        {/* Breadcrumbs come from the explorer, already trimmed to its root. */}
        {!search.trim() && explorer.breadcrumbs.length > 1 && (
          <nav className="flex items-center gap-1 text-xs text-gray-500 flex-wrap">
            {explorer.breadcrumbs.map((crumb, index) => (
              <span key={crumb.nodeId} className="flex items-center gap-1">
                {index > 0 && <ChevronRight size={12} />}
                <button
                  type="button"
                  onClick={() => openNode(crumb.nodeId)}
                  className="hover:text-primary-600"
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>
        )}

        {(status === 'loading' || searching) && (
          <p className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        )}

        {status === 'error' && (
          <p className="text-sm text-red-600 py-4">
            {explorer.error ?? 'The files could not be loaded.'}
          </p>
        )}

        {status !== 'loading' && (
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {!search.trim() &&
              nodes.map((node) => (
                <button
                  key={node.nodeId}
                  type="button"
                  onClick={() => openNode(node.nodeId)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50"
                  data-testid={`file-picker-folder-${node.nodeId}`}
                >
                  <Folder size={15} className="text-primary-600 flex-shrink-0" />
                  <span className="flex-1 truncate">{node.name}</span>
                  <ChevronRight size={14} className="text-gray-300" />
                </button>
              ))}

            {files.map((item) => {
              const Icon = iconFor(item);
              return (
                <label
                  key={item.id}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    data-testid={`file-picker-file-${item.id}`}
                  />
                  <Icon size={15} className="text-gray-400 flex-shrink-0" />
                  <span className="flex-1 truncate">{item.filename}</span>
                </label>
              );
            })}

            {/*
              An entity-type folder — Events, Vendors, Venues — holds the
              application's records and arrives a page at a time. Without this
              the picker shows the first fifty events and offers no way to the
              rest, which reads as "that event has no files".
            */}
            {!search.trim() && hasMoreNodes && (
              <button
                type="button"
                onClick={loadMoreNodes}
                disabled={loadingMore}
                className="w-full px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                data-testid="file-picker-load-more"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}

            {nodes.length === 0 && files.length === 0 && (
              <p className="px-3 py-6 text-sm text-gray-400 text-center">
                {search.trim() ? 'Nothing matched.' : 'This folder is empty.'}
              </p>
            )}
          </div>
        )}
      </div>
    </EntityModal>
  );
}
